// Formatting in Russian. Money arrives from the API as exact decimals (e.g. 1234.56).

const rub = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });
const rub2 = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const int = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });
const pct = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

// Replace the narrow no-break space Intl uses with a regular no-break space for font support.
const nbsp = (s) => s.replace(/ /g, ' ');

export function formatMoney(v, { kopecks = false } = {}) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  return nbsp((kopecks ? rub2 : rub).format(kopecks ? n : Math.round(n))) + ' ₽';
}

export function formatNumber(v) {
  if (v === null || v === undefined) return '—';
  return nbsp(int.format(Number(v)));
}

export function formatPercent(v) {
  if (v === null || v === undefined) return '—';
  return nbsp(pct.format(Number(v))) + '%';
}

// Signed delta for comparison badges: "+12,4%" / "−3,1%".
export function formatDelta(v, unit = '%') {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  const sign = n > 0 ? '+' : n < 0 ? '−' : '';
  return `${sign}${nbsp(pct.format(Math.abs(n)))}${unit === 'pp' ? ' п.п.' : '%'}`;
}

export function formatDate(iso) {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}.${m}.${y}`;
}

const MONTHS = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

export function formatDateShort(iso) {
  if (!iso) return '—';
  const [, m, d] = String(iso).slice(0, 10).split('-');
  return `${Number(d)} ${MONTHS[Number(m) - 1]}`;
}

// Time in Moscow (the WB time zone every date in MARQIT uses).
export function formatTimeMsk(ts) {
  if (!ts) return '—';
  return new Intl.DateTimeFormat('ru-RU', { timeZone: 'Europe/Moscow', hour: '2-digit', minute: '2-digit' }).format(new Date(ts));
}

export function formatDateTimeMsk(ts) {
  if (!ts) return '—';
  return nbsp(
    new Intl.DateTimeFormat('ru-RU', {
      timeZone: 'Europe/Moscow', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    }).format(new Date(ts))
  );
}

// "сегодня в 10:40" / "вчера в 17:10" / "25.09 в 09:20"
export function formatRelativeMsk(ts, todayIso) {
  if (!ts) return '—';
  const day = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Moscow' }).format(new Date(ts));
  const time = formatTimeMsk(ts);
  const today = todayIso || mskToday();
  if (day === today) return `сегодня в ${time}`;
  if (day === addDays(today, -1)) return `вчера в ${time}`;
  return `${formatDate(day).slice(0, 5)} в ${time}`;
}

export function mskToday() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Moscow' }).format(new Date());
}

export function addDays(iso, n) {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + n));
  return dt.toISOString().slice(0, 10);
}

export function daysBetween(fromIso, toIso) {
  const a = Date.parse(fromIso + 'T00:00:00Z');
  const b = Date.parse(toIso + 'T00:00:00Z');
  return Math.round((b - a) / 86400000) + 1;
}

export function pluralize(n, one, few, many) {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return many;
  if (b > 1 && b < 5) return few;
  if (b === 1) return one;
  return many;
}

export function greeting(now = new Date()) {
  const h = Number(new Intl.DateTimeFormat('ru-RU', { timeZone: 'Europe/Moscow', hour: '2-digit', hour12: false }).format(now));
  if (h >= 5 && h < 12) return 'Доброе утро';
  if (h >= 12 && h < 18) return 'Добрый день';
  if (h >= 18 && h < 23) return 'Добрый вечер';
  return 'Доброй ночи';
}

export function apiError(err, fallback = 'Не удалось выполнить запрос. Повторите попытку.') {
  return err?.response?.data?.error || (err?.response ? fallback : 'Нет связи с сервером. Проверьте подключение к интернету.');
}

// Reasons a profit figure is still preliminary, in the user's words.
export const MISSING_LABELS = {
  cost_price: 'не задана себестоимость части товаров',
  tax_not_configured: 'не заполнены налоговые настройки',
  tax_not_supported: 'расчёт налога для выбранного режима не реализован',
  advertising: 'нет данных о рекламных расходах',
  finance_coverage: 'финансовые отчёты WB ещё не охватывают весь период',
  unknown_operations: 'есть неразобранные операции',
};

// The user's access in a few words: "Администратор", "Пробный период · до 15.10.2026", "Полный доступ".
export function planLabel(profile) {
  if (!profile) return '';
  if (profile.role === 'admin') return 'Администратор';
  const until = profile.expires_at ? ` · до ${formatDate(profile.expires_at)}` : '';
  return profile.is_trial ? `Пробный период${until}` : `Полный доступ${until}`;
}

// "27.09.2026" for a single day, "21.09.2026–27.09.2026" for a range.
export function formatRange(from, to) {
  return from === to ? formatDate(from) : `${formatDate(from)}–${formatDate(to)}`;
}
